import { axiosInstance, HttpStatusCode } from '@/plugins/axios';
import { authComputed } from '@/store/helpers';
import { showErrorToast, showSuccessToast } from '@/utils/toast';
import errorTracker from '@/plugins/errorTracker';
import { getSupportTeamById } from '@/services/supportTeams.service';

const networkError = 'Network error. Check your connection.';

export const moduleState = {
  client: null,
  clientLogs: null,
  currentClientStatus: [],
  clientCreditEligibility: [],
  accountDetails: null,
  loading: false,
  clientSysData: [],
  clientCallsSubscription: [],
  promoDetails: null,
  contract: null,
  sendHistory: [],
  supressionStatus: [],
  subsidyByCountyDetails: [],
  pagination: {
    currentPage: 1,
    pageSize: 20,
    totalRecords: 0,
  },
  salesPeople: [],
};

export const mutations = {
  SET_CURRENT_CLIENT(state, value) {
    state.client = value;
  },
  SET_ACCOUNT_DETAILS(state, value) {
    state.accountDetails = value;
  },
  // this sets the client status after an update to the status. Represents the client stats before / after a change to the status
  SET_CLIENT_STATUS(state, status) {
    state.client.status = status;
  },
  // this sets the *current* status of a client after a get. Represents the status of a client as is.
  SET_CURRENT_CLIENT_STATUS(state, status) {
    state.currentClientStatus = status;
  },
  SET_CREDIT_ELIGIBILITY(state, val) {
    state.clientCreditEligibility = val;
  },
  SET_LOADING(state, isLoading) {
    state.loading = isLoading;
  },
  SET_CLIENT_SYS_DATA(state, val) {
    state.clientSysData = val;
  },
  SET_CLIENT_CALLS_SUBSCRIPTION(state, val) {
    state.clientCallsSubscription = val;
  },
  SET_PROMO_DETAILS(state, details) {
    state.promoDetails = details;
  },
  SET_CONTRACT(state, data) {
    state.contract = data;
  },
  SET_SEND_HISTORY(state, data) {
    state.sendHistory = data;
  },
  SET_SUPPRESSION_STATUS(state, data) {
    state.supressionStatus = data;
  },
  SET_SALES_PEOPLE(state, data) {
    state.salesPeople = data;
  },

  setAcctDetailFirstName(state, val) {
    state.accountDetails.firstName = val;
  },
  setAcctDetailLastName(state, val) {
    state.accountDetails.lastName = val;
  },
  setAcctDetailFullName(state, val) {
    state.accountDetails.fullName = val;
  },

  setAcctDetailShortName(state, val) {
    state.accountDetails.shortName = val;
  },

  setAcctDetailLicense(state, val) {
    state.accountDetails.license = val;
  },

  setAcctDetailAddress1(state, val) {
    state.accountDetails.address1 = val;
  },
  setAcctDetailAddress2(state, val) {
    state.accountDetails.address2 = val;
  },
  setAcctDetailCity(state, val) {
    state.accountDetails.city = val;
  },
  setAcctDetailState(state, val) {
    state.accountDetails.state = val;
  },
  setAcctDetailZip(state, val) {
    state.accountDetails.zip = val;
  },

  setAcctDetailPhone(state, val) {
    state.accountDetails.phone = val;
  },
  setAcctDetailEmail(state, val) {
    state.accountDetails.email = val;
  },

  setAcctDetailStatus(state, val) {
    state.accountDetails.status = val;
  },

  setAcctDetailLoyaltyLevel(state, val) {
    state.accountDetails.loyaltyLevel = val;
  },

  setAcctDetailParentCompany(state, val) {
    state.accountDetails.agency = val;
  },

  setAcctDetailConsentCompanyId(state, val) {
    if (!state.accountDetails) {
      state.accountDetails = {};
    }
    state.accountDetails.consentCompanyId = val;
  },

  setAcctDetailAgencyEmail(state, val) {
    state.accountDetails.agencyEmail = val;
  },

  setAcctDetailSalesforceId(state, val) {
    state.accountDetails.salesforceId = val;
  },

  setAcctDetailTimeZone(state, val) {
    state.accountDetails.timeZoneOffset = val;
  },

  setAcctDetailDNCEmail(state, val) {
    state.accountDetails.dncEmail = val;
  },
  SET_SUBSIDY_BY_COUNTY(state, data) {
    state.subsidyByCountyDetails = data;
  },
  setPagination(state, value) {
    state.pagination = value;
  },
  setCurrentPage(state, newValue) {
    state.pagination.currentPage = newValue;
  },
  setPageSize(state, newValue) {
    state.pagination.pageSize = newValue;
  },
  setLeadState(state, val) {
    state.subsidyByCountyDetails.leadState = val;
  },
  setCounty(state, val) {
    state.subsidyByCountyDetails.county = val;
  },
  setProduct(state, val) {
    state.subsidyByCountyDetails.product = val;
  },
  setSubsidy(state, val) {
    state.subsidyByCountyDetails.subsidy = val;
  },
  setSubsidyCap(state, val) {
    state.subsidyByCountyDetails.subsidyCap = val;
  },
  clearData(state) {
    state.subsidyByCountyDetails = [];
  },
};

export const getters = {
  client: state => state.client,
  clientFirstName: state => state.client?.firstName,
  accountDetails: state => state.accountDetails,
  loading: state => state.loading,
  promoDetails: state => state.promoDetails,
  clientCallsSubscription: state => state.clientCallsSubscription,
  salesPeople: state => state.salesPeople,
  subsidyByCountyDetails: state => state.subsidyByCountyDetails,
  hasClient: state => !!state.client?.id,
  clientId: state => state.client?.id ?? null,
  address: state => ({
    city: state.client?.city ?? null,
    state: state.client?.state ?? null,
    zip: state.client?.zip ?? null,
  }),
  clientStatus: state => state.client?.status ?? null,
  statusMap: state => state.client?.statusMap ?? null,
  clientTimezone: state => state.client?.timeZone ?? null,
  isCorporate: state => state.client?.clientType === 'CORPORATE',
  isAgent: state => state.client?.clientType == null || state.client.clientType === 'AGENT',
  isFolio: state => state.client?.farmersFolio && state.client?.parentCompany === 'FARMERS',
  isStateFarm: state => state.client?.parentCompany === 'STATEFARM',

  supportPhone: state => {
    return getSupportTeamById(state.client?.salesSupportTeamId).phone;
  },

  supportEmail: state => {
    return getSupportTeamById(state.client?.salesSupportTeamId).email;
  },

  supportHours: state => {
    return getSupportTeamById(state.client?.salesSupportTeamId).hours;
  },

  billingType: state => state.client?.reactivationInfo?.billingType,

  isCcBilling: state => state.client?.reactivationInfo?.billingType === 'CC',

  isEligibleForReactivation: state =>
    state.client?.reactivationInfo?.isEligibleForReactivation || false,
};

export const actions = {
  async fetchClient({ dispatch, state }, { clientId }) {
    if (state.client && state.client.id === clientId) {
      return state.client;
    }

    const response = await axiosInstance.get(`/api/clients/${clientId}`).catch(err => err);

    if (response instanceof Error) {
      const res = response.response;
      switch (res && res.status) {
        case 401:
          throw res.status;
        case 404:
          showErrorToast('Invalid ClientID');
          return;
        default:
          console.error(response);
          throw response;
      }
    }

    const client = response.data;
    dispatch('setClient', client);
    return client;
  },

  async fetchClientAccountDetails({ commit }, clientId) {
    commit('SET_LOADING', true);
    const response = await axiosInstance.get(`/api/clients/${clientId}/account`).catch(err => err);

    if (response instanceof Error) {
      // TODO: Error handling
      commit('SET_LOADING', false);
      console.error(response);
      throw response;
    }

    commit('SET_ACCOUNT_DETAILS', response.data);
    commit('SET_LOADING', false);
    return response.data;
  },

  async updateAccountDetails({ commit, state }) {
    commit('SET_LOADING', true);
    const response = await axiosInstance
      .post(`/api/Clients/${state.client.id}/agent`, state.accountDetails)
      .catch(err => err);

    if (response instanceof Error) {
      const { response: res } = response;
      if (!res) {
        showErrorToast(networkError);
      } else {
        showErrorToast(res.data);
      }

      commit('SET_LOADING', false);
      console.error(response);
      return Promise.reject(response);
    }

    commit('SET_LOADING', false);
    return response.data;
  },

  getClient({ dispatch, rootState }, { clientId }) {
    if (clientId == null) {
      return axiosInstance
        .get(`/api/clients/auth/${rootState.auth.user.idToken.oid}`)
        .then(response => {
          dispatch('setClient', response.data);
        })
        .catch(err => {
          console.error(err);
          return Promise.reject(err);
        });
    } else {
      return axiosInstance
        .get(`/api/clients/${clientId}`)
        .then(response => {
          dispatch('setClient', response.data);
        })
        .catch(err => {
          console.error(err);
          return Promise.reject(err);
        });
    }
  },

  async setClient({ commit, dispatch }, client) {
    if (
      authComputed.isAdmin ||
      authComputed.isClientServices ||
      authComputed.isClientServicesAdmin
    ) {
      axiosInstance.defaults.headers.common.clientId = client.id;
    }

    window.document.title = `${client.fullName} - Client Admin`;

    commit('SET_CURRENT_CLIENT', client);

    // Initialize subsidy data for StateFarm clients
    if (client.parentCompany === 'STATEFARM') {
      try {
        await dispatch('subsidy/getEnabledPercentSubsidyStates', null, {
          root: true,
        });
      } catch (error) {
        console.error('Failed to initialize subsidy data for StateFarm client:', error);
      }
    }
  },

  clearClient({ commit }) {
    // Undoes 'setClient'
    delete axiosInstance.defaults.headers.common.clientId;
    window.document.title = `Client Admin`;
    commit('SET_CURRENT_CLIENT', {});
  },

  async createDefaultClient({ dispatch }, clientInfo) {
    const response = await dispatch('request/post', {
      url: '/api/clients/default',
      data: clientInfo,
      errorMessage: err =>
        err?.response?.data ?? 'Failed to create default client. Please contact support.',
      loadingMutation: 'client/SET_LOADING',
    });

    return response?.data ?? null;
  },

  async setClientStatus({ commit, state }, status) {
    const previousStatus = state.client.status;

    function resetStatus(errorMessage) {
      showErrorToast(errorMessage);

      commit('SET_CLIENT_STATUS', previousStatus);
      console.error(errorMessage);
      return errorMessage;
    }
    commit('SET_LOADING', true);
    const response = await axiosInstance
      .put(`/api/status/${state.client.id}/${status}`, {})
      .catch(resetStatus);

    if (response.status !== 200) {
      resetStatus(
        `Invalid response status or data. Response status: ${response.status}, Data (rows affected): ${response.data}`
      );
    } else {
      commit('SET_CLIENT_STATUS', response.data);
    }
    commit('SET_LOADING', false);
  },

  async activateCashCapAccount({ commit, state, dispatch }) {
    const response = await axiosInstance
      .post(`/api/status/${state.client.id}/activate-cashcap`)
      .catch(err => {
        showErrorToast(
          err?.response?.data ?? 'Failed to activate account. Please contact support.'
        );
        return null;
      });

    if (!response) {
      return false;
    }

    commit('SET_CLIENT_STATUS', response.data.status);
    showSuccessToast('Account activated.');

    // The server already deleted the CashCapActivation notification; clear it locally right
    // away so the banner disappears immediately without waiting on a network round trip, then
    // refresh in the background to reconcile with anything else that may have changed.
    commit('notifications/CLEAR_CASH_CAP_ACTIVATION_NOTIFICATIONS', null, { root: true });
    dispatch('notifications/fetchClientNotifications', null, { root: true });

    return true;
  },

  async getConsentCompanyId({ dispatch, state, commit }) {
    const response = await dispatch('request/get', {
      url: `/api/consentcompany/names?clientId=${state.client.id}`,
      errorMessage: 'Failed to get consent company id',
    });

    if (response) {
      commit('setAcctDetailConsentCompanyId', response.data?.[0]?.id);
    }
    return response;
  },

  async setConsentCompanyId({ dispatch, state, commit }, consentCompanyId) {
    const response = dispatch('request/put', {
      url: `/api/consentcompany/${consentCompanyId}/${state.client.id}`,
      errorMessage: 'Failed to set consent company client',
    });

    if (response) {
      commit('setAcctDetailConsentCompanyId', consentCompanyId);
    }
    return response;
  },

  async getLogs(_, { clientId, pagination }) {
    const params = {
      currentPage: pagination.currentPage,
      pageSize: pagination.pageSize,
      sortBy: pagination.sortBy,
      startDate: pagination.startDates[0],
      endDate: pagination.startDates[1],
      last45Days: pagination.last45Days,
    };
    try {
      const response = await axiosInstance.get(`/api/clients/${clientId}/logs`, { params });
      return response?.data ?? null;
    } catch (error) {
      if (error.response) {
        showErrorToast(error.response.data);
      } else {
        showErrorToast(error);
      }
      return null;
    }
  },

  async getReactivationLogs(_, clientId) {
    const response = await axiosInstance.get(`/api/clients/${clientId}/ReactivationLogs`);
    const data = response.data;

    return data;
  },

  async updateReactivationLog(_, { clientId, reactivationLog }) {
    const response = await axiosInstance.post(
      `/api/clients/${clientId}/ReactivationLog`,
      reactivationLog
    );

    if (response instanceof Error) {
      errorTracker.trackException({
        exception: response,
      });
      console.error(response);

      showErrorToast('Failed to update reactivation attribute.');
    }
  },

  async getSalesPeople({ commit }) {
    const response = await axiosInstance.get('/api/clients/SALES_PEOPLE/options');
    const data = response.data;

    commit('SET_SALES_PEOPLE', data);
  },

  async syncToSalesForce({ commit }, { clientId }) {
    commit('SET_LOADING', true);
    try {
      const response = await axiosInstance.post(`/api/clients/salesforce/sync/${clientId}`);
      showSuccessToast('Sync Successful');
      commit('SET_LOADING', false);
      return response;
    } catch (error) {
      console.error(error);
      showErrorToast(error);
      commit('SET_LOADING', false);
    }
  },

  async getClientStatus({ commit }, { clientId }) {
    axiosInstance
      .get(`/api/status/${clientId}`)
      .then(response => {
        if (response.status === 200) {
          commit('SET_CURRENT_CLIENT_STATUS', response.data);
        }
      })
      .catch(err => {
        console.error(err);
        showErrorToast(err);
      });
  },

  async getCreditEligibility({ commit }, { clientId, leadId }) {
    axiosInstance
      .get(`/api/clients/${clientId}/can_credit_lead?leadId=${leadId}`)
      .then(response => {
        if (response.status === 200) {
          commit('SET_CREDIT_ELIGIBILITY', response.data);
        }
      })
      .catch(err => {
        console.error(err);
        showErrorToast(err);
      });
  },

  async getClientContractSnapshot({ commit }, clientId) {
    const response = await axiosInstance.get(`/api/clients/contract/${clientId}`).catch(err => err);

    commit('SET_CONTRACT', response.data || {});
  },

  async downloadContract(_, clientId) {
    await axiosInstance
      .get(`/api/clients/contract/download/${clientId}`, {
        responseType: 'blob',
      })
      .then(({ data }) => {
        const downloadUrl = window.URL.createObjectURL(new Blob([data]));
        const link = document.createElement('a');
        link.href = downloadUrl;
        link.setAttribute('download', 'contract.pdf');
        document.body.appendChild(link);
        link.click();
        link.remove();
      })
      .catch(err => err);
  },

  async getClientSystemData({ commit }, { clientId }) {
    axiosInstance
      .get(`/api/clients/${clientId}/account_sys`)
      .then(response => {
        if (response.status === 200) {
          commit('SET_CLIENT_SYS_DATA', response.data);
        }
      })
      .catch(err => {
        console.error(err);
        showErrorToast(err);
      });
  },

  async getClientCallSubscription({ commit }, { clientId }) {
    axiosInstance
      .get(`/api/clients/${clientId}/calls-subscription-account`)
      .then(response => {
        if (response.status === 200) {
          commit('SET_CLIENT_CALLS_SUBSCRIPTION', response.data);
        }
      })
      .catch(err => {
        console.error(err);
        showErrorToast(err);
      });
  },

  async getStateFarmSubsidy(
    { state, commit },
    { clientId, leadState, sortBy, county, product, subsidy, subsidyCap }
  ) {
    const params = {
      clientId,
      leadState,
      sortBy,
      county,
      product,
      subsidy,
      subsidyCap,
      pageNum: state.pagination.currentPage,
      pageSize: state.pagination.pageSize,
    };

    try {
      const response = await axiosInstance.get(`/api/clients/${clientId}/subsidy`, { params });
      if (response.status === HttpStatusCode.Ok) {
        commit('SET_SUBSIDY_BY_COUNTY', response.data.items ?? {});
        commit('setPagination', response.data.metaData);
      }
    } catch (err) {
      console.error(err);
      showErrorToast(err);
    }
  },

  async getClientPromoDetails({ commit, state }, clientId) {
    const id = clientId ?? state.client.id;
    commit('SET_LOADING', true);

    try {
      const response = await axiosInstance.get(`/api/clients/${id}/promos`);
      commit('SET_PROMO_DETAILS', response.data);
    } catch (err) {
      console.error(err);
      showErrorToast('Failed to get promotion details.');
    } finally {
      commit('SET_LOADING', false);
    }
  },

  async updateClientPromoDetails({ commit }, promoDetails) {
    commit('SET_LOADING', true);

    try {
      const response = await axiosInstance.post(
        `/api/Promo/${promoDetails.clientId}/promo`,
        promoDetails
      );
      commit('SET_PROMO_DETAILS', response.data);
    } catch (err) {
      console.error(err);
      if (!err.response) {
        showErrorToast(networkError);
      } else {
        showErrorToast(err.response.data.message);
      }
      throw err;
    } finally {
      commit('SET_LOADING', false);
    }
  },

  async insertClientPromoDetails({ commit }, promoDetails) {
    commit('SET_LOADING', true);

    try {
      const response = await axiosInstance.post(
        `/api/Promo/${promoDetails.clientId}/createpromo`,
        promoDetails
      );
      commit('SET_PROMO_DETAILS', response.data);
    } catch (err) {
      console.error(err);
      if (!err.response) {
        showErrorToast(networkError);
      } else {
        showErrorToast(err.response.data.message);
      }
      throw err;
    } finally {
      commit('SET_LOADING', false);
    }
  },

  async zeroClientPromoAmounts({ commit }, promoDetails) {
    commit('SET_LOADING', true);

    try {
      const response = await axiosInstance.post(
        `/api/Promo/${promoDetails.clientId}/zeropromo`,
        promoDetails
      );
      commit('SET_PROMO_DETAILS', response.data);
    } catch (err) {
      console.error(err);
      if (!err?.response?.data) {
        showErrorToast(networkError);
      } else {
        showErrorToast(err.response.data?.message ?? err.response.data);
      }
      throw err;
    } finally {
      commit('SET_LOADING', false);
    }
  },

  async getClientAccountDetails({ commit }, { clientId }) {
    const response = await axiosInstance.get(`/api/clients/${clientId}/account`).catch(err => {
      console.error(err);
      showErrorToast(err);
      return null;
    });
    commit('SET_ACCOUNT_DETAILS', response.data);

    return response.data;
  },

  async getClientSendHistory({ dispatch }, clientId) {
    const response = await dispatch('request/get', {
      url: `/api/clients/${clientId}/emailHistory`,
      errorMessage: 'Failed to get send grid history',
    });

    const issues = [].concat(
      response.data.blockedEmails,
      response.data.bouncedEmails,
      response.data.invalidEmails,
      response.data.spamEmails
    );

    const messages = response.data.messages.map(x => {
      const item = {
        date: x.last_event_time,
        status: x.status,
        from_email: x.from_email,
        subject: x.subject,
        last_event_time: x.last_event_time,
        clicks_count: x.clicks_count,
        opens_count: x.opens_count,
        msg_id: x.msg_id,
      };

      return item;
    });

    return {
      messages,
      issues,
      email: response.data.emailAddress,
    };
  },

  async getClientEmailStatus({ commit, dispatch }, clientId) {
    const response = await dispatch('request/get', {
      url: `/api/clients/${clientId}/emailStatus`,
      errorMessage: 'Failed to get email status',
    });

    return response.data;
  },

  async getEmailTimeline({ dispatch }, { clientId, messageId }) {
    const response = await dispatch('request/get', {
      url: `/api/clients/${clientId}/emailTimeline/${messageId}`,
      errorMessage: 'Failed to get email timeline',
    });

    return response.data;
  },

  async getSpamReports({ dispatch }, { noCache = null }) {
    const response = await dispatch('request/get', {
      url: `/api/clients/spam-reports`,
      params: { 'no-cache': noCache },
      errorMessage: 'Failed to get Spam Reports',
    });

    return response.data;
  },

  async removeSpamReport({ dispatch }, { emails = [] }) {
    if (emails === null || emails.length === 0) {
      return;
    }

    await dispatch('request/delete', {
      url: `/api/clients/spam-reports`,
      data: { emails },
      errorMessage: 'Failed to remove Spam Reports',
    });
  },

  reset({ commit }) {
    commit('SET_CURRENT_CLIENT', { statusMap: [], timeZone: 0 });
    commit('SET_ACCOUNT_DETAILS', {});
  },
};
